import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import RefreshIcon from '@mui/icons-material/Refresh';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';

const TABLE_COLUMNS = [
  { key: 'account_name', label: 'Account Name', minWidth: 160, maxWidth: 360, defaultWidth: 220 },
  { key: 'scope', label: 'Scope', minWidth: 180, maxWidth: 540, defaultWidth: 300 },
  { key: 'sync_listings', label: 'Sync Listings', minWidth: 120, maxWidth: 220, defaultWidth: 130 },
  { key: 'shop', label: 'Shop', minWidth: 220, maxWidth: 520, defaultWidth: 280 },
  { key: 'currency', label: 'Currency', minWidth: 90, maxWidth: 180, defaultWidth: 110 },
  { key: 'last_synced', label: 'Last Synced', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'actions', label: 'Actions', minWidth: 120, maxWidth: 220, defaultWidth: 130 },
];

const ShopsPage = () => {
  const { fetchWithAuth } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [formError, setFormError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [reconnectingShopId, setReconnectingShopId] = React.useState(null);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.etsy-shops',
  });

  const loadAccounts = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/accounts`);
      if (!resp.ok) {
        throw new Error('Failed to load Etsy accounts');
      }
      const payload = await resp.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setError(err.message || 'Failed to load shops');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const startOAuth = React.useCallback(
    async ({ shopId } = {}) => {
      const params = new URLSearchParams();
      if (shopId) {
        params.set('shop_id', shopId);
      }
      const url =
        params.toString().length > 0
          ? `${config.apiBaseUrl}/etsy/oauth/start?${params.toString()}`
          : `${config.apiBaseUrl}/etsy/oauth/start`;
      const resp = await fetchWithAuth(url, { method: 'POST' });
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to initiate Etsy OAuth');
      }
      const payload = await resp.json();
      if (payload.authorization_url) {
        window.open(payload.authorization_url, '_blank', 'noopener');
      }
      return payload;
    },
    [fetchWithAuth],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      await startOAuth();
      setDialogOpen(false);
    } catch (err) {
      setFormError(err.message || 'Failed to start OAuth');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReconnect = async (shopId) => {
    if (!shopId) {
      return;
    }
    setReconnectingShopId(shopId);
    try {
      await startOAuth({ shopId });
    } catch (err) {
      setError(err.message || 'Failed to restart OAuth');
    } finally {
      setReconnectingShopId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Etsy Shops</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAccounts}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Shop
          </Button>
        </Stack>
      </Box>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading shops..." />
              ) : items.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No Etsy shops connected yet." />
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.name || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {item.scope || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.sync_listings ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {item.shop ? (
                        <Stack spacing={0}>
                          <Typography variant="subtitle2">{item.shop.title || item.shop.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.shop.external_id}
                          </Typography>
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{item.shop?.currency_code || '—'}</TableCell>
                    <TableCell>
                      {item.shop?.last_synced_at ? new Date(item.shop.last_synced_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleReconnect(item.shop?.id)}
                        disabled={!item.shop?.id || reconnectingShopId === item.shop?.id}
                      >
                        {reconnectingShopId === item.shop?.id ? 'Opening…' : 'Reconnect'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Connect Etsy Shop</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click “Start OAuth” to launch Etsy&apos;s authorization page with the configured app
            credentials.
          </Typography>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            Start OAuth
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopsPage;
